import { BaseTool, IToolContext, IToolManifest } from '../../../core/tools/interfaces/ITool';
import type { ICommand } from '../../../core/commands/ICommand';
import { CompositeCommand } from '../../../core/commands/ICommand';
import { CreateEdgeCommand } from '../../../core/commands/CreateEdgeCommand';
import { useDiagramStore } from '../../../stores/diagramStore';
import i18n from '@/i18n/config';
import { bindGenogramToolName } from './utils/toolNameI18n';

export class GroupAsSiblingsTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'genogram-group-as-siblings',
    name: i18n.t('genogram:tools.groupAsSiblings', { defaultValue: 'Group as Siblings' }) as string,
    icon: '👨‍👩‍👧‍👦',
    category: 'create',
    description: 'Create sibling relationships between selected people',
    version: '1.0.0',
    supportedDiagramTypes: ['genogram' as any],
    requiresSelection: true,
    minSelection: 3,
    ui: {
      toolbar: false,
      contextMenu: false,
      propertyPanel: false,
      sidebar: false,
      floatingPanel: true,
      statusBar: false
    },
    applicability: {
      contextTypes: ['person'],
      diagramTypes: ['genogram'],
    },
    capabilities: {
      modifiesShapes: false,
      createsShapes: false,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: true,
      deletesConnectors: false,
      requiresUserInput: false,
      supportsMultiSelect: true,
      supportsBatchOperations: true
    },
    shortcuts: [
      { key: '1', description: 'Group as siblings (when 3+ people selected)' }
    ]
  };

  constructor() {
    super();
    bindGenogramToolName(this.manifest, 'tools.groupAsSiblings', 'Group as Siblings');
  }

  canExecute(context: IToolContext): boolean {
    if (!super.canExecute(context)) return false;

    // Check that selection is 3+ people
    const summary = context.selectionSummary;
    return summary?.contextType === 'person' && context.selection.size >= 3;
  }

  execute(): ICommand | null {
    if (!this.context) return null;

    const nodeIds = Array.from(this.context.selection);
    if (nodeIds.length < 2) return null;

    const state = useDiagramStore.getState();
    const commands: ICommand[] = [];

    // Create sibling edges between consecutive pairs
    for (let i = 0; i < nodeIds.length - 1; i++) {
      const fromId = nodeIds[i];
      const toId = nodeIds[i + 1];

      const fromNode = state.nodes.find(n => n.id === fromId);
      const toNode = state.nodes.find(n => n.id === toId);

      if (!fromNode || !toNode) continue;

      const fromPos = { x: fromNode.x, y: fromNode.y };
      const toPos = { x: toNode.x, y: toNode.y };

      // Determine anchor points based on relative positions
      let fromAnchor: 'top' | 'right' | 'bottom' | 'left' = 'right';
      let toAnchor: 'top' | 'right' | 'bottom' | 'left' = 'left';

      if (Math.abs(fromPos.x - toPos.x) < 30) {
        // Vertically aligned
        if (fromPos.y < toPos.y) {
          fromAnchor = 'bottom';
          toAnchor = 'top';
        } else {
          fromAnchor = 'top';
          toAnchor = 'bottom';
        }
      } else if (fromPos.x < toPos.x) {
        // Left to right
        fromAnchor = 'right';
        toAnchor = 'left';
      } else {
        // Right to left
        fromAnchor = 'left';
        toAnchor = 'right';
      }

      const edgeCommand = new CreateEdgeCommand({
        type: 'sibling',
        from: fromId,
        to: toId,
        category: 'genogram',
        fromAnchor,
        toAnchor,
      });

      commands.push(edgeCommand);
    }

    if (commands.length === 0) return null;

    return new CompositeCommand('Group as Siblings', commands);
  }
}
